import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButton } from '@angular/material/button';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { GAnalyticsService } from '../../../services/g-analytics.service';
import { AuthService } from '../../../core/auth/auth.service';
import { MetaData, NotificationResponse, Notifications } from './notifications.model';
import { ToastrService } from 'ngx-toastr';
import { TranslocoService } from '@ngneat/transloco';
import { MessagingService } from '../../../services/messaging-service.service';
import { UserService } from 'app/core/user/user.service';

@Component({
  selector: 'app-notifications',
  templateUrl: './bar-notifications.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'notifications',
})
export class BarNotificationsComponent implements OnInit, OnDestroy {
  notifications: Notifications[] = []; // Use new Notification model
  @ViewChild('notificationsOrigin') private _notificationsOrigin: MatButton;
  @ViewChild('notificationsPanel')
  private _notificationsPanel: TemplateRef<any>;
  private _overlayRef: OverlayRef;
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private currentPage: number = 1;
  public metaData: MetaData = null;
  loading: boolean = false;
  @Output() itemClicked = new EventEmitter();

  /**
   * Constructor
   */
  constructor(
    private _changeDetectorRef: ChangeDetectorRef,
    private _notificationsService: NotificationsService,
    private _overlay: Overlay,
    private _toaster: ToastrService,
    public transloco: TranslocoService,
    private _viewContainerRef: ViewContainerRef,
    private _router: Router,
    private googleServices: GAnalyticsService,
    private _messagingService: MessagingService,
    private userService: UserService,
  ) {}

  // -----------------------------------------------------------------------------------------------------
  // @ Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------

  /**
   * On init
   */
  ngOnInit(): void {
    this._messagingService.requestPermission();
    this._messagingService.listen();
    this._notificationsService.initialize();
    // Subscribe to notification changes
    this._notificationsService.notifications$
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe((notifications: NotificationResponse) => {
        if (notifications) {
          // Assign meta_data only if it's defined
          this.metaData = notifications.meta_data;

          if (this.metaData.current_page === 1) {
            // Add new notifications on top of the list
            this.addNotification(notifications.data, 'above');
          } else {
            // Add notifications at the bottom of the list
            this.addNotification(notifications.data, 'below');
            this.currentPage = this.metaData.current_page;
          }
        } else {
          // Handle the case where meta_data is undefined or null
          this.metaData = {
            current_page: 0,
            total_pages: 0,
            per_page: 0,
            unread_notifications_count: 0,
            total_items: 0,
          }; // Default values
        }

        // Trigger change detection
        this._changeDetectorRef.markForCheck();
      });
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();

    // Dispose the overlay
    if (this._overlayRef) {
      this._overlayRef.dispose();
    }
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Open the notifications panel
   */
  openPanel(): void {
    this.googleServices.setEvent('Notifications_my-projects', 'Interactivity');

    if (!this._notificationsPanel || !this._notificationsOrigin) {
      return;
    }

    // Create the overlay if it doesn't exist
    if (!this._overlayRef) {
      this._createOverlay();
    }

    // Attach the portal to the overlay
    this._overlayRef.attach(new TemplatePortal(this._notificationsPanel, this._viewContainerRef));
  }

  /**
   * Close the messages panel
   */
  closePanel(): void {
    this._overlayRef.detach();
  }

  /**
   * Mark a notification as read
   */
  markAllAsRead(): void {
    // Mark all as read
    this.googleServices.setEvent('ViewAllNotifications_my-projects', 'Interactivity');
    this._router.navigate(['/notifications']);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  makeItRead(notification): void {
    this.googleServices.setEvent('notificationReaded_my-projects', 'Interactivity');
    this.itemClicked.emit(notification);
    this.onItemClicked(notification);
    this.closePanel();
  }

  /**
   * Create the overlay
   */
  private _createOverlay(): void {
    this._overlayRef = this._overlay.create({
      hasBackdrop: true,
      backdropClass: 'fuse-backdrop-on-mobile',
      scrollStrategy: this._overlay.scrollStrategies.block(),
      positionStrategy: this._overlay
        .position()
        .flexibleConnectedTo(this._notificationsOrigin._elementRef.nativeElement)
        .withLockedPosition()
        .withPush(true)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'end',
            originY: 'bottom',
            overlayX: 'end',
            overlayY: 'top',
          },
        ]),
    });

    // Detach the overlay on backdrop click
    this._overlayRef.backdropClick().subscribe(() => {
      this._overlayRef.detach();
    });
  }

  private async onItemClicked(notification: Notifications) {
    const userId = await this.userService.getUserId();

    this._notificationsService.markAsRead(userId, notification.id).subscribe({
      next: () => {
        // After marking as read, refetch the notifications
        this._notificationsService.getNotifications().subscribe({
          next: async () => {
            const targetUrl =
              await this._notificationsService.resolveNotificationTargetUrl(notification);
            const navigationTarget = this.isSameDomainUrl(targetUrl) ? '_self' : '_blank';
            window.open(targetUrl, navigationTarget);
          },
          error: (error) => {
            // Handle error during notifications refetch
            this._toaster.error(
              error.message || this.transloco.translate('Something went wrong, please try again.'),
            );
          },
        });
      },
      error: (error) => {
        // Handle error during marking as read
        this._toaster.error(
          error.message || this.transloco.translate('Something went wrong, please try again.'),
        );
      },
    });
  }

  private isSameDomainUrl(url: string): boolean {
    try {
      return new URL(url, window.location.origin).origin === window.location.origin;
    } catch {
      return true;
    }
  }

  addNotification(newNotifications: Notifications[], location: string) {
    if (this.notifications.length == 0) {
      this.notifications = newNotifications;
    } else if (newNotifications.length > 0) {
      let newArr = [];
      for (let notification of newNotifications) {
        if (this.notifications.find((element: Notifications) => element.id === notification.id)) {
          if (location == 'above') {
            // if same notification found it means we have added the new ones to list
            break;
          }
          // if location is selected below means we ignore same ones till we find new
        } else {
          // add the notifications that we don't have
          newArr.push(notification);
        }
      }

      if (location == 'above') {
        this.notifications = newArr.concat(this.notifications);
      } else {
        this.notifications = this.notifications.concat(newArr);
      }
    }
  }

  onScroll(): void {
    if (this.currentPage < this.metaData.total_pages && !this.loading) {
      this.loading = true;

      this._notificationsService.getNotifications(this.currentPage + 1, true).subscribe({
        next: () => {
          // Notifications successfully fetched, the UI will be updated by subscription.
          this.loading = false;
        },
        error: (error) => {
          this.loading = false; // Stop the loading spinner if an error occurs

          // Show error toast notification
          this._toaster.error(
            error.message || this.transloco.translate('Something went wrong, please try again.'),
          );
        },
      });
    }
  }
}
